/** Order-get pricing payload; optional until commons ships the field. */
export type OrderPrice = {
  amount: number;
  currency: string;
  tax?: {
    description?: string;
  };
  freeTextField?: string;
};

const DEFAULT_CURRENCY = 'EUR';
const FALLBACK_LOCALE = 'de-DE';

const toBcp47Locale = (locale: string): string => locale.replace(/_/g, '-');

/** Locale the runtime can format without falling back to its default. */
export const resolveNumberLocale = (locale: string): string => {
  const requested = toBcp47Locale(locale);
  const supported = Intl.NumberFormat.supportedLocalesOf([requested, FALLBACK_LOCALE], {
    localeMatcher: 'lookup',
  });

  return supported[0] ?? FALLBACK_LOCALE;
};

const isIso4217Currency = (currency: string): boolean => {
  try {
    Intl.NumberFormat('en', { style: 'currency', currency }).format(0);
    return true;
  } catch {
    return false;
  }
};

/** Currency from order-get `pricing.currency`. Invalid codes fall back to EUR. */
export const resolveCurrency = (currency: string): string => {
  const normalized = currency.trim().toUpperCase();
  return normalized && isIso4217Currency(normalized) ? normalized : DEFAULT_CURRENCY;
};

export const isOrderPrice = (pricing: unknown): pricing is OrderPrice =>
  typeof pricing === 'object' &&
  pricing !== null &&
  typeof (pricing as OrderPrice).amount === 'number' &&
  typeof (pricing as OrderPrice).currency === 'string';

const formatWithCurrency = (amount: number, currency: string, locale: string): string => {
  const resolvedLocale = resolveNumberLocale(locale);
  const { minimumFractionDigits, maximumFractionDigits } = new Intl.NumberFormat(resolvedLocale, {
    style: 'currency',
    currency,
  }).resolvedOptions();

  const number = new Intl.NumberFormat(resolvedLocale, {
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(amount);
  const currencySymbol =
    new Intl.NumberFormat(resolvedLocale, {
      style: 'currency',
      currency,
      currencyDisplay: 'narrowSymbol',
    })
      .formatToParts(amount)
      .find((part) => part.type === 'currency')?.value ?? currency;

  return `${number}\u00a0${currencySymbol}`;
};

export const formatPrice = (amount: number, currency: string, locale: string): string =>
  formatWithCurrency(amount, resolveCurrency(currency), locale);
